import { useEffect, useState } from 'react'
import { Loader2, Filter, X } from 'lucide-react'
import Toast from '../components/Toast'
import { vulnerabilitiesApi, type UnpublishedVulnerability } from '../services/vulnerabilitiesApi'
import { useAuthStore } from '../stores/authStore'
import { useProjectsStore } from '../stores/projectsStore'
import { useTenantsStore } from '../stores/tenantsStore'

type ToastState = {
  message: string
  type: 'success' | 'error'
} | null

interface Filters {
  tenantId: string
  projectId: string
  assetId: string
  scanId: string
  severity: string
  status: string
}

const Notifications = () => {
  const { getUserRole } = useAuthStore()
  const userRole = getUserRole()

  const projects = useProjectsStore(state => state.projects)
  const fetchProjects = useProjectsStore(state => state.fetchProjects)

  const tenants = useTenantsStore(state => state.tenants)
  const fetchTenants = useTenantsStore(state => state.fetchTenants)

  const [unpublishedVulnerabilities, setUnpublishedVulnerabilities] = useState<UnpublishedVulnerability[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedVulnerability, setSelectedVulnerability] =
    useState<UnpublishedVulnerability | null>(null)
  const [toast, setToast] = useState<ToastState>(null)
  const [submittingId, setSubmittingId] = useState<string | null>(null)
  const [vulnTab, setVulnTab] = useState<'general' | 'pos'>('general')
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<Filters>({
    tenantId: '',
    projectId: '',
    assetId: '',
    scanId: '',
    severity: '',
    status: '',
  })
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
  })

  const fetchUnpublishedVulnerabilities = async () => {
    if (userRole !== 'ADMIN') return

    setLoading(true)
    setError(null)
    try {
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
      }

      // Add filters if they are set
      if (filters.tenantId) params.tenantId = filters.tenantId
      if (filters.projectId) params.projectId = filters.projectId
      if (filters.assetId) params.assetId = filters.assetId
      if (filters.scanId) params.scanId = filters.scanId
      if (filters.severity) params.severity = filters.severity
      if (filters.status) params.status = filters.status

      const response = await vulnerabilitiesApi.getUnpublishedVulnerabilities(params)
      
      if (response.success && response.data) {
        setUnpublishedVulnerabilities(response.data.data || [])
        setPagination(prev => ({
          ...prev,
          total: response.data?.total || 0,
          page: response.data?.page || 1,
        }))
      } else {
        setError('Failed to fetch unpublished vulnerabilities')
      }
    } catch (err) {
      setError('An error occurred while fetching vulnerabilities')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const closeVulnerabilityModal = () => {
    setSelectedVulnerability(null)
    setVulnTab('general')
  }

  const openVulnerabilityModal = (vulnerability: UnpublishedVulnerability) => {
    setSelectedVulnerability(vulnerability)
    setVulnTab('general')
  }

  useEffect(() => {
    if (userRole === 'ADMIN') {
      fetchUnpublishedVulnerabilities()
    }
  }, [userRole, pagination.page, pagination.limit])

  useEffect(() => {
    if (userRole === 'ADMIN') {
      fetchProjects()
      fetchTenants()
    }
  }, [userRole, fetchProjects, fetchTenants])

  useEffect(() => {
    if (error) {
      setToast({ message: error, type: 'error' })
    }
  }, [error])

  const handlePublish = async (vulnerabilityId: string) => {
    setSubmittingId(vulnerabilityId)
    try {
      const response = await vulnerabilitiesApi.updatePublishedStatus(vulnerabilityId, {
        published: true,
        publishedStatus: 'PUBLISHED',
      })

      if (response.success) {
        setToast({
          message: 'Vulnerability published successfully.',
          type: 'success',
        })
        // Refresh the list
        fetchUnpublishedVulnerabilities()
      } else {
        setToast({ message: 'Failed to publish vulnerability.', type: 'error' })
      }
    } catch (err) {
      setToast({ message: 'Failed to publish vulnerability.', type: 'error' })
    } finally {
      setSubmittingId(null)
    }
  }

  const handleReject = async (vulnerabilityId: string) => {
    setSubmittingId(vulnerabilityId)
    try {
      const response = await vulnerabilitiesApi.updatePublishedStatus(vulnerabilityId, {
        published: false,
        publishedStatus: 'REJECTED',
      })

      if (response.success) {
        setToast({
          message: 'Vulnerability rejected successfully.',
          type: 'success',
        })
        // Refresh the list
        fetchUnpublishedVulnerabilities()
      } else {
        setToast({ message: 'Failed to reject vulnerability.', type: 'error' })
      }
    } catch (err) {
      setToast({ message: 'Failed to reject vulnerability.', type: 'error' })
    } finally {
      setSubmittingId(null)
    }
  }

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }))
    fetchUnpublishedVulnerabilities()
  }

  const clearFilters = () => {
    setFilters({
      tenantId: '',
      projectId: '',
      assetId: '',
      scanId: '',
      severity: '',
      status: '',
    })
    setPagination(prev => ({ ...prev, page: 1 }))
    // Fetch will be triggered by useEffect watching filters
  }

  const closeToast = () => {
    setToast(null)
    setError(null)
  }

  if (userRole !== 'ADMIN') {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Access restricted
        </h2>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          You do not have permission to view unpublished vulnerabilities.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Notifications
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Review and manage unpublished vulnerabilities awaiting your
            approval.
          </p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Filter className="h-4 w-4 mr-2" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {showFilters && (
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tenant
              </label>
              <select
                value={filters.tenantId}
                onChange={(e) => handleFilterChange('tenantId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Tenants</option>
                {tenants.map(tenant => (
                  <option key={tenant._id} value={tenant._id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Project
              </label>
              <select
                value={filters.projectId}
                onChange={(e) => handleFilterChange('projectId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Projects</option>
                {projects.map(project => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Severity
              </label>
              <select
                value={filters.severity}
                onChange={(e) => handleFilterChange('severity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Severities</option>
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
                <option value="INFO">Info</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Statuses</option>
                <option value="FIXED">Fixed</option>
                <option value="NOT_FIXED">Not Fixed</option>
                <option value="READY_TO_REVALIDATE">Ready to Revalidate</option>
                <option value="OUT_OF_SCOPE">Out of Scope</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex justify-end space-x-3">
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <X className="h-4 w-4 mr-2" />
              Clear Filters
            </button>
            <button
              onClick={applyFilters}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
        <div className="px-6 py-4">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Unpublished Vulnerabilities
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            These vulnerabilities have not been published yet. Review the
            details, then publish or reject each finding.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Vulnerability Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Tenant
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Project
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-10 text-center text-gray-500 dark:text-gray-400"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Loading vulnerabilities...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && unpublishedVulnerabilities.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-10 text-center text-gray-500 dark:text-gray-400"
                  >
                    No unpublished vulnerabilities at the moment.
                  </td>
                </tr>
              )}

              {!loading &&
                unpublishedVulnerabilities.map(vulnerability => {
                  const publishedStatus =
                    vulnerability.publishedStatus || 'UNPUBLISHED'

                  return (
                    <tr
                      key={vulnerability._id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {vulnerability.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {tenants.find(t => t._id === vulnerability.tenantId)?.name || 'Unknown tenant'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {vulnerability.project?.name || 'Unknown project'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            publishedStatus === 'PUBLISHED'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : publishedStatus === 'REJECTED'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                          }`}
                        >
                          {publishedStatus.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-3">
                          <button
                            onClick={() =>
                              openVulnerabilityModal(vulnerability)
                            }
                            className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handlePublish(vulnerability._id)}
                            disabled={submittingId === vulnerability._id}
                            className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                          >
                            Publish
                          </button>
                          <button
                            onClick={() => handleReject(vulnerability._id)}
                            disabled={submittingId === vulnerability._id}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedVulnerability && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeVulnerabilityModal}
          />
          <div className="relative z-10 w-full max-w-4xl mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {selectedVulnerability.name}
              </h3>
              <button
                onClick={closeVulnerabilityModal}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            <div className="px-6 pt-4">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                  <button
                    onClick={() => setVulnTab('general')}
                    className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${
                      vulnTab === 'general'
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    General
                  </button>
                  <button
                    onClick={() => setVulnTab('pos')}
                    className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${
                      vulnTab === 'pos'
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    POC
                  </button>
                </nav>
              </div>
            </div>

            <div className="px-6 py-4 overflow-y-auto flex-1">
              {vulnTab === 'general' && (
                <div className="space-y-6 text-sm">
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Scan Type
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {selectedVulnerability.scan?.type || 'Manual'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Testing Type
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {selectedVulnerability.scan?.testingType?.replace(/_/g, ' ') || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Severity
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          <span 
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: selectedVulnerability.severity === 'CRITICAL' ? '#C00000'
                                : selectedVulnerability.severity === 'HIGH' ? '#FF0000'
                                : selectedVulnerability.severity === 'MEDIUM' ? '#FFC000'
                                : selectedVulnerability.severity === 'LOW' ? '#00B050'
                                : '#00B0F0',
                              color: selectedVulnerability.severity === 'MEDIUM' ? '#111827' : '#ffffff'
                            }}
                          >
                            {selectedVulnerability.severity}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          CVSS
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {typeof selectedVulnerability.cvss === 'number'
                            ? selectedVulnerability.cvss.toFixed(1)
                            : 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          SLA (Days)
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {selectedVulnerability.sla || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Status
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {selectedVulnerability.status?.replace(/_/g, ' ') || 'Unknown'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Created On
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {new Date(selectedVulnerability.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Created By
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {selectedVulnerability.createdBy
                            ? `${selectedVulnerability.createdBy.firstName} ${selectedVulnerability.createdBy.lastName}`
                            : 'Unknown'}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700" />
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-gray-500 dark:text-gray-400 mb-1">
                          Tenant
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {tenants.find(t => t._id === selectedVulnerability.tenantId)?.name || 'Unknown'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400 mb-1">
                          Project
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {selectedVulnerability.project?.name || 'Unknown'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400 mb-1">
                          Asset
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {selectedVulnerability.asset?.name || 'Unknown'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400 mb-1">
                          Asset Type
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {selectedVulnerability.asset?.assetType?.replace(/_/g, ' ') || 'Unknown'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400 mb-1">
                          Scan
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {selectedVulnerability.scan?.name || 'Unknown'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400 mb-1">
                          Scan Status
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {selectedVulnerability.scan?.status?.replace(/_/g, ' ') || 'Unknown'}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700" />
                  
                  <div className="space-y-1">
                    <div className="text-gray-900 dark:text-white font-medium">
                      Synopsis
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">
                      {selectedVulnerability.synopsis}
                    </p>
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700" />
                  
                  <div className="space-y-1">
                    <div className="text-gray-900 dark:text-white font-medium">
                      Impact
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">
                      {selectedVulnerability.impact}
                    </p>
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700" />
                  
                  <div className="space-y-1">
                    <div className="text-gray-900 dark:text-white font-medium">
                      Recommendation
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">
                      {selectedVulnerability.recommendation}
                    </p>
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700" />
                  
                  <div className="space-y-1">
                    <div className="text-gray-900 dark:text-white font-medium">
                      Reference
                    </div>
                    {selectedVulnerability.reference && selectedVulnerability.reference.length > 0 ? (
                      <ul className="list-disc pl-5 text-gray-600 dark:text-gray-300">
                        {selectedVulnerability.reference.map((ref, index) => (
                          <li key={index}>
                            <a
                              href={ref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                            >
                              {ref}
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-600 dark:text-gray-300">No references provided.</p>
                    )}
                  </div>
                </div>
              )}
              {vulnTab === 'pos' && (
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-900 dark:text-white font-medium mb-2">
                      Caption
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      Proof of Concept for {selectedVulnerability.name}
                    </div>
                  </div>
                  <div className="w-full overflow-hidden rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    <img
                      src="https://miro.medium.com/v2/resize:fit:1200/1*j8licN2V1DOxeu_x7tEyng.jpeg"
                      alt="POC reference"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={closeVulnerabilityModal}
                className="px-4 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={closeToast} />
      )}
    </div>
  )
}

export default Notifications
